import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface HoverPreviewGroupValue {
  /** True when any chip in the group currently has its popover open. */
  anyOpen: boolean;
  setOpen: (id: string, open: boolean) => void;
}

const HoverPreviewGroupContext = createContext<HoverPreviewGroupValue | null>(null);

/**
 * Coordinates the hover previews of a row of chips (e.g. the agent settings
 * bar, the Advanced bar, the Attachments bar) so that opening any one chip's
 * editor popover suppresses every chip's hover card. Without this, a hover
 * preview that lingers over a neighbouring chip's open dropdown would cover it.
 *
 * Wrap a chip bar in this provider and let each chip register its popover open
 * state via {@link useHoverPreviewGroup}. Standalone chips (no provider) keep
 * their original self-only suppression.
 */
export function HoverPreviewGroup({ children }: { children: ReactNode }) {
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => new Set());

  const setOpen = useCallback((id: string, open: boolean) => {
    setOpenIds((prev) => {
      if (open === prev.has(id)) return prev;
      const next = new Set(prev);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo<HoverPreviewGroupValue>(
    () => ({ anyOpen: openIds.size > 0, setOpen }),
    [openIds, setOpen],
  );

  return (
    <HoverPreviewGroupContext.Provider value={value}>{children}</HoverPreviewGroupContext.Provider>
  );
}

/**
 * Registers a chip's popover `open` state with the surrounding
 * {@link HoverPreviewGroup} and returns whether any chip in the group is open
 * (including this one). Chips suppress their hover card whenever this is true.
 * Returns `false` when used outside a group, preserving standalone behavior.
 */
export function useHoverPreviewGroup(open: boolean): boolean {
  const ctx = useContext(HoverPreviewGroupContext);
  const id = useId();
  const setOpen = ctx?.setOpen;

  useEffect(() => {
    setOpen?.(id, open);
  }, [setOpen, id, open]);
  useEffect(() => () => setOpen?.(id, false), [setOpen, id]);

  return ctx?.anyOpen ?? false;
}
